import { MetadataStateHandler } from './MetadataStateHandler';
import { Messaging, LocaleProvider, ThemeProvider } from 'oskari-ui/util';
import { MetadataSearchContainer } from './view/MetadataSearchContainer';
import React from 'react';
import ReactDOM from 'react-dom';
import { COVERAGE_LAYER_ID } from '../../mapping/mapmodule/plugin/layers/coveragetool/CoverageHelper';
import './publisher/MetadataSearchTool';
import './request/MetadataSearchRequest';
import './request/MetadataSearchRequestHandler';
import './event/MetadataSearchResultEvent';
/**
 * @class Oskari.mapframework.bundle.metadatasearch.MetadataSearchBundleInstance
 *
 * Main component and starting point for the "metadata catalogue" functionality.
 * Provides metadata catalogue search functionality for the map.
 *
 * See Oskari.mapframework.bundle.metadatasearch.MetadataCatalogueBundle for bundle definition.
 *
 */
export var METADATA_BUNDLE_LOCALIZATION_ID = 'catalogue.bundle.metadatasearch';
var COVERAGE_FEATURE_STYLE = {
  stroke: {
    color: 'rgba(211, 187, 27, 0.8)',
    width: 2
  },
  fill: {
    color: 'rgba(255,222,0, 0.6)'
  }
};
Oskari.clazz.define('Oskari.catalogue.bundle.metadatasearch.MetadataSearchBundleInstance',
/**
 * @method create called automatically on construction
 * @static
 */
function () {
  var _this = this;
  var sandboxName = (this.conf ? this.conf.sandbox : null) || 'sandbox';
  this.sandbox = Oskari.getSandbox(sandboxName);
  this.sandbox.register(this);
  this.started = false;
  this.plugins = {};
  this.loc = Oskari.getMsg.bind(null, 'catalogue.bundle.metadatasearch');
  this.optionAjaxUrl = null;
  this.searchAjaxUrl = null;
  this.initUrls();
  this.id = 'oskari_metadatasearch_tabpanel_header';
  this._vectorLayerId = 'METADATASEARCH_VECTORLAYER';
  this.handler = new MetadataStateHandler(this);
  this.contentElement = document.createElement('div');
  this.handler.addStateListener(function () {
    _this.renderSearch();
  });
}, {
  /**
   * @static
   * @property __name
   */
  __name: 'catalogue.bundle.metadatasearch',
  /**
   * @method getName
   * @return {String} the name for the component
   */
  getName: function getName() {
    return this.__name;
  },
  getVectorLayerId: function getVectorLayerId() {
    return this._vectorLayerId;
  },
  /**
   * @method setSandbox
   * @param {Oskari.Sandbox} sandbox
   * Sets the sandbox reference to this component
   */
  setSandbox: function setSandbox(sandbox) {
    this.sandbox = sandbox;
  },
  /**
   * @method getSandbox
   * @return {Oskari.Sandbox}
   */
  getSandbox: function getSandbox() {
    return this.sandbox;
  },
  initUrls: function initUrls() {
    if (this.conf && this.conf.optionUrl) {
      this.optionAjaxUrl = this.conf.optionUrl;
    } else {
      this.optionAjaxUrl = Oskari.urls.getRoute('GetMetadataSearchOptions');
    }
    if (this.conf && this.conf.searchUrl) {
      this.searchAjaxUrl = this.conf.searchUrl;
    } else {
      this.searchAjaxUrl = Oskari.urls.getRoute('GetMetadataSearch');
    }
  },
  /**
   * @method start
   * implements BundleInstance protocol start method
   */
  start: function start() {
    var _this2 = this,
      _this$conf,
      _this$conf2;
    if (this.started) {
      return;
    }
    this.started = true;
    Object.keys(this.eventHandlers).forEach(function (eventName) {
      _this2.sandbox.registerForEventByName(_this2, eventName);
    });
    var metadataSearchRequestHandler = Oskari.clazz.create('Oskari.catalogue.bundle.metadatasearch.request.MetadataSearchRequestHandler', this);
    this.sandbox.requestHandler('MetadataSearchRequest', metadataSearchRequestHandler);

    // Default tab priority
    if (this.conf && typeof ((_this$conf = this.conf) === null || _this$conf === void 0 ? void 0 : _this$conf.priority) === 'number') {
      this.tabPriority = this.conf.priority;
    }
    if (((_this$conf2 = this.conf) === null || _this$conf2 === void 0 ? void 0 : _this$conf2.noUI) === true) {
      // bundle started by published map
      return;
    }

    // draw ui
    this.createUi();
  },
  /**
   * @method init
   * implements Module protocol init method - does nothing atm
   */
  init: function init() {
    return null;
  },
  /**
   * @method update
   * implements BundleInstance protocol update method - does
   * nothing atm
   */
  update: function update() {},
  /**
   * @method onEvent
   * @param {Oskari.mapframework.event.Event} event a Oskari event
   * object
   * Event is handled forwarded to correct #eventHandlers if found
   * or discarded if not.
   */
  onEvent: function onEvent(event) {
    var handler = this.eventHandlers[event.getName()];
    if (!handler) {
      return;
    }
    return handler.apply(this, [event]);
  },
  /**
   * @property {Object} eventHandlers
   * @static
   */
  eventHandlers: {
    'DrawingEvent': function DrawingEvent(event) {
      if (event.getId() !== this.getName() || event.getIsFinished() !== true || this.drawCoverage) {
        return;
      }
      var coverageFeature = event.getGeoJson();
      this.handler.getController().updateCoverageFeature(JSON.stringify(coverageFeature));
    },
    'userinterface.ExtensionUpdatedEvent': function userinterfaceExtensionUpdatedEvent(event) {
      var isShown = event.getViewState() !== 'close';
      var name = event.getExtension().getName();

      // ExtensionUpdateEvents are fired a lot, only let metadatacatalogue extension event to be handled when enabled
      if (![this.getName(), 'Search'].includes(name)) {
        // wasn't me or disabled -> do nothing
        return;
      }
      if (!isShown && this.drawCoverage === false || event.getViewState() === 'close') {
        this._teardownMetaSearch();
      }
      if (this.getName() === name && isShown) {
        // own flyout so Search.TabChangedEvent doesn't trigger load options
        this.handler.loadOptions();
      }
    },
    'Search.TabChangedEvent': function SearchTabChangedEvent(event) {
      if (event.getNewTabId() !== this.id) {
        this._teardownMetaSearch();
      } else {
        this.handler.loadOptions();
        this.removeFeaturesFromMap();
      }
    },
    'MetadataSearchResultEvent': function MetadataSearchResultEvent(event) {
      // TODO: test me via RPC
      if (this.conf.noUI === true) {
        // bundle started by published map --> not handle event
        return;
      }
      if (event.hasSuccess()) {
        this.handler.getController().updateSearchResult(event.getResults());
      } else {
        Messaging.error(Oskari.getMsg(METADATA_BUNDLE_LOCALIZATION_ID, 'advancedSearch.metadataSearchserviceError'));
      }
    },
    'AfterMapLayerAddEvent': function AfterMapLayerAddEvent(event) {
      this.handler.getController().updateSelectedLayers();
    },
    'AfterMapLayerRemoveEvent': function AfterMapLayerRemoveEvent(event) {
      this.handler.getController().updateSelectedLayers();
    },
    'FeatureEvent': function FeatureEvent(event) {
      var _event$getFeatures, _event$getFeatures2;
      // coverage removed by pressing the plugin button on map -> reset our own state as well.
      if (event.getOperation() === 'remove' && (_event$getFeatures = event.getFeatures()) !== null && _event$getFeatures !== void 0 && _event$getFeatures.some(function (feature) {
        return feature.layerId === COVERAGE_LAYER_ID;
      })) {
        this.handler.updateDisplayedCoverageId(null);
        return;
      }

      // detect metadata coverage added to map by the id in it's properties and update bookkeeping accordingly.
      if (event.getOperation() === 'add' && (_event$getFeatures2 = event.getFeatures()) !== null && _event$getFeatures2 !== void 0 && _event$getFeatures2.some(function (feature) {
        return feature.layerId === COVERAGE_LAYER_ID;
      })) {
        var addedDisplayedCoverageId = this.getDisplayedMetadaCoverageIdFromFeatureEvent(event) || null;
        if (addedDisplayedCoverageId) {
          this.handler.updateDisplayedCoverageId(addedDisplayedCoverageId);
        }
      }
    }
  },
  getDisplayedMetadaCoverageIdFromFeatureEvent: function getDisplayedMetadaCoverageIdFromFeatureEvent(event) {
    var features = event === null || event === void 0 ? void 0 : event.getFeatures().filter(function (feature) {
      return feature.layerId === COVERAGE_LAYER_ID;
    });
    if (!features || !features.length > 0) {
      return null;
    }
    var geojsonFeatures = features.flatMap(function (feature) {
      var _feature$geojson;
      return feature === null || feature === void 0 || (_feature$geojson = feature.geojson) === null || _feature$geojson === void 0 ? void 0 : _feature$geojson.features;
    });
    var found = geojsonFeatures === null || geojsonFeatures === void 0 ? void 0 : geojsonFeatures.find(function (feature) {
      var _feature$properties;
      return !!(feature !== null && feature !== void 0 && (_feature$properties = feature.properties) !== null && _feature$properties !== void 0 && _feature$properties.displayedMetadataCoverageId);
    });
    return found ? found.properties.displayedMetadataCoverageId : null;
  },
  /**
   * @method _teardownMetaSearch
   * @private
   * Tears down meta data search when changing tab or closing flyout
   */
  _teardownMetaSearch: function _teardownMetaSearch() {
    this.handler.getController().advancedSearchCoverageCancelDrawing();
  },
  /**
   * @method addCoverageFeatureToMap
   * Adds coverage feature to map.
   *
   * @param {String} geom WKT representation of the geometry of the feature to add
   */
  addCoverageFeatureToMap: function addCoverageFeatureToMap(geom) {
    var requestName = 'MapModulePlugin.AddFeaturesToMapRequest';
    this.getSandbox().postRequestByName(requestName, [geom, {
      layerId: this.getVectorLayerId(),
      clearPrevious: true,
      layerOptions: null,
      centerTo: true,
      featureStyle: COVERAGE_FEATURE_STYLE
    }]);
  },
  /**
   * @method removeFeaturesFromMap
   * Removes features from map.
   *
   * @param {String} identifier the identifier
   * @param {String} value the identifier value
   */
  removeFeaturesFromMap: function removeFeaturesFromMap(identifier, value) {
    this.sandbox.postRequestByName('MapModulePlugin.RemoveFeaturesFromMapRequest', [identifier, value, this.getVectorLayerId()]);
  },
  /**
   * @method startDrawing
   * Start drawing a feature for searching by coverage
   */
  startDrawing: function startDrawing() {
    this.sandbox.postRequestByName('DrawTools.StartDrawingRequest', [this.getName(), 'Box', {
      style: COVERAGE_FEATURE_STYLE
    }]);
  },
  /**
   * @method stopDrawing
   * Stop drawing
   */
  stopDrawing: function stopDrawing() {
    this.sandbox.postRequestByName('DrawTools.StopDrawingRequest', [this.getName(), true]);
  },
  /**
   * @method addMapLayer
   * Add layer to map
   *
   * @param {number} layerId id of the layer to add
   */
  addMapLayer: function addMapLayer(layerId) {
    this.sandbox.postRequestByName('AddMapLayerRequest', [layerId]);
  },
  /**
   * @method removeMapLayer
   * Remove layer from map and set visibility to true
   *
   * @param {number} layerId id of the layer to add
   */
  removeMapLayer: function removeMapLayer(layerId) {
    this.sandbox.postRequestByName('RemoveMapLayerRequest', [layerId]);
  },
  /**
   * @method stop
   * implements BundleInstance protocol stop method
   */
  stop: function stop() {
    var _this3 = this;
    var sandbox = this.sandbox;
    Object.keys(this.eventHandlers).forEach(function (eventName) {
      sandbox.unregisterFromEventByName(_this3, eventName);
    });
    var reqBuilder = Oskari.requestBuilder('userinterface.RemoveExtensionRequest');
    this.sandbox.request(this, reqBuilder(this));
    this.sandbox.unregisterStateful(this.mediator.bundleId);
    this.sandbox.unregister(this);
    this.started = false;
  },
  /**
   * @method getTitle
   * @return {String} localized text for the title of the component
   */
  getTitle: function getTitle() {
    return Oskari.getMsg(METADATA_BUNDLE_LOCALIZATION_ID, 'tabTitle');
  },
  /**
   * @method getDescription
   * @return {String} localized text for the description of the
   * component
   */
  getDescription: function getDescription() {
    return this.loc('desc');
  },
  /**
   * @method createUi
   * (re)creates the UI for "metadata search" functionality
   */
  createUi: function createUi() {
    // Metadata search tab
    this.renderSearch();
    var reqBuilder = Oskari.requestBuilder('Search.AddTabRequest');
    if (typeof reqBuilder === 'function') {
      var title = Oskari.getMsg(METADATA_BUNDLE_LOCALIZATION_ID, 'tabTitle');
      var priority = this.tabPriority;
      this.sandbox.request(this, reqBuilder(title, this.contentElement, priority, this.id));
    } else {
      // add a tile and flyout if search is not present on the appsetup
      this.__addTileAndFlyout();
    }
  },
  renderSearch: function renderSearch() {
    ReactDOM.render(/*#__PURE__*/React.createElement(LocaleProvider, {
      value: {
        bundleKey: METADATA_BUNDLE_LOCALIZATION_ID
      }
    }, /*#__PURE__*/React.createElement(ThemeProvider, null, /*#__PURE__*/React.createElement(MetadataSearchContainer, {
      state: this.handler.getState(),
      controller: this.handler.getController()
    }))), this.contentElement);
  },
  /* ----------- Tile and Flyout ------------- */
  __addTileAndFlyout: function __addTileAndFlyout() {
    var request = Oskari.requestBuilder('userinterface.AddExtensionRequest')(this);
    this.getSandbox().request(this, request);
    // attach content to flyout when divmanazer has set up root element
    this.plugins['Oskari.userinterface.Flyout'].getEl().append(this.contentElement);
  },
  /**
   * @method startExtension
   * implements Oskari.userinterface.Extension protocol startExtension method
   * Creates a flyout and a tile:
   * Oskari.mapframework.bundle.layerlist.Flyout
   * Oskari.mapframework.bundle.layerlist.Tile
   */
  startExtension: function startExtension() {
    var title = this.getTitle();
    this.plugins['Oskari.userinterface.Tile'] = Oskari.clazz.create('Oskari.userinterface.extension.DefaultTile', this, {
      title: title
    });
    this.plugins['Oskari.userinterface.Flyout'] = Oskari.clazz.create('Oskari.userinterface.extension.DefaultFlyout', this, {
      title: title
    });
  },
  /**
   * @method stopExtension
   * implements Oskari.userinterface.Extension protocol stopExtension method
   * Clears references to flyout and tile
   */
  stopExtension: function stopExtension() {
    this.plugins['Oskari.userinterface.Flyout'] = null;
    this.plugins['Oskari.userinterface.Tile'] = null;
  },
  /**
   * @method getPlugins
   * implements Oskari.userinterface.Extension protocol getPlugins method
   * @return {Object} references to flyout and tile
   */
  getPlugins: function getPlugins() {
    return this.plugins;
  }
  /* ----------- /Tile and Flyout ------------- */
}, {
  /**
   * @property {String[]} protocol
   * @static
   */
  protocol: ['Oskari.bundle.BundleInstance', 'Oskari.mapframework.module.Module']
});